import type {
	DOMAttributes,
	TargetedPointerEvent,
	TargetedWheelEvent,
} from "preact";
import { useEffect, useRef, useState } from "preact/compat";
import {
	maxImageZoom,
	type ImageZoomOffset,
	type PictureConfiguration,
} from "@brickme/project-core/src/model/picture.ts";
import type {
	GenericSize,
	PixelPosition,
} from "@brickme/project-core/src/model/geom.ts";
import {
	arePositionsClose,
	addPositions,
	subtractPositions,
} from "@brickme/project-core/src/model/geom.ts";
import roundAndConstrainImageZoomOffset from "@brickme/project-core/src/utils/round-and-constrain-image-zoom-offset.ts";
import { useRenderMode } from "../picture-render/render-mode-context.tsx";
import { usePicture } from "./picture-edit-context.tsx";
import {
	getCanvasPixelPosition,
	canvasPositionToBrick,
} from "../utils/canvas.ts";
import { getTouchDistance } from "../utils/touch.ts";

const imageZoomStep = 0.02;

const sourceRenderModeId = "zoom-drag";
const wheelImageZoomSourceRenderId = "zoom-wheel";

function getImageZoomOffsetFree(
	{ width: sourceWidth, height: sourceHeight }: GenericSize<number>,
	{
		imageZoom,
		basePlateSize,
		numberOfBasePlates,
	}: Pick<
		PictureConfiguration,
		"imageZoom" | "basePlateSize" | "numberOfBasePlates"
	>,
) {
	const numberOfBricks = {
		width: numberOfBasePlates.width * basePlateSize,
		height: numberOfBasePlates.height * basePlateSize,
	};
	const xScale = numberOfBricks.width / sourceWidth;
	const yScale = numberOfBricks.height / sourceHeight;
	const useScale = Math.max(xScale, yScale);
	const cropWidth = numberOfBricks.width / useScale / imageZoom;
	const cropHeight = numberOfBricks.height / useScale / imageZoom;
	const zoomFreeX = sourceWidth - cropWidth;
	const zoomFreeY = sourceHeight - cropHeight;
	const bricksPerPixel = numberOfBricks.width / cropWidth;
	return {
		widthDimensionMax: Math.round((zoomFreeX * bricksPerPixel) / 2),
		heightDimensionMax: Math.round((zoomFreeY * bricksPerPixel) / 2),
	};
}

type ZoomDragStart = {
	readonly mousePosition: PixelPosition;
	readonly imageZoomOffset: ImageZoomOffset;
};

type ImageZoomCanvasControlsOptions = {
	readonly sourceImageDimensions: GenericSize<number>;
	readonly onStart?: () => void;
	readonly onEnd?: () => void;
	// Opt-in: track multi-touch pointers so two fingers pinch-zoom picture.imageZoom.
	// Off by default so other consumers (pix-a-pic, franchise) are unaffected.
	readonly enableTwoFingerZoom?: boolean;
	// Opt-in: during a single-finger pan, report the offset here each move instead
	// of committing it to the picture context. The caller drives the canvas live and
	// we patch the picture once on drag-end. Avoids a per-move re-render of every
	// picture consumer (the whole editor subtree) which stutters panning on phones.
	// Absent = commit each move as before (unchanged for other consumers).
	readonly onLiveOffset?: (offset: ImageZoomOffset) => void;
	// Opt-in: only show the source image in the canvas while pressed but still, then
	// flip back to bricks the moment a drag starts (and stay bricks for the rest of
	// the gesture); never show source for wheel/pinch zoom. Default false keeps the
	// original "source while dragging/zooming" behaviour for other consumers.
	readonly canvasSourceOnPressHold?: boolean;
};

// Below this absolute imageZoom delta we skip the patch, to avoid a flood of
// re-builds/re-renders while pinching.
const pinchZoomThreshold = 0.01;

type ImageZoomCanvasControls = {
	readonly isDragging: boolean;
	readonly imageZoomCanvasHandlers: Pick<
		DOMAttributes<EventTarget>,
		| "onPointerDown"
		| "onPointerUp"
		| "onPointerMove"
		| "onPointerLeave"
		| "onPointerCancel"
		| "onWheel"
	>;
};

function useImageZoomCanvasControls({
	sourceImageDimensions,
	onStart,
	onEnd,
	enableTwoFingerZoom = false,
	onLiveOffset,
	canvasSourceOnPressHold = false,
}: ImageZoomCanvasControlsOptions): ImageZoomCanvasControls {
	const { picture, patchPicture, patchPictureDebounced } = usePicture();
	const { imageZoomOffset, numberOfBasePlates, basePlateSize } = picture;
	const { requestRenderMode, withdrawRenderModeRequest } = useRenderMode();

	const [zoomDraggingStart, setZoomDraggingStart] = useState<
		ZoomDragStart | undefined
	>(undefined);
	// Latest offset from a deferred (onLiveOffset) drag, committed once on drag-end.
	const pendingLiveOffset = useRef<ImageZoomOffset | undefined>(undefined);
	// Whether the drag's source-render request is still live (press-hold mode drops
	// it on the first move so a moving drag shows bricks, not the source).
	const dragSourceActive = useRef(false);
	// TODO: Don't request low quality render or show hand drag icon if no space possible.
	// i.e. aspect ratio of source must equal aspect ratio number of base plates and zoom == 1
	function beginZoomDrag(mousePosition: PixelPosition) {
		onStart?.();
		requestRenderMode(sourceRenderModeId, "source");
		dragSourceActive.current = true;
		setZoomDraggingStart({
			mousePosition,
			imageZoomOffset,
		});
	}

	function onZoomDragStart(e: TargetedPointerEvent<Element>) {
		const mousePosition = getCanvasPixelPosition(e);
		if (!mousePosition) {
			return;
		}
		beginZoomDrag(mousePosition);
	}

	function onZoomDragMove(e: TargetedPointerEvent<Element>) {
		if (!zoomDraggingStart) {
			console.error("Invalid state - moving while start not called");
			return;
		}

		const mousePosition = getCanvasPixelPosition(e);
		if (!mousePosition) {
			return;
		}

		const mouseOffset = subtractPositions(
			mousePosition,
			zoomDraggingStart.mousePosition,
		);

		const brickOffset = canvasPositionToBrick(e.currentTarget, mouseOffset, {
			width: numberOfBasePlates.width * basePlateSize,
			height: numberOfBasePlates.height * basePlateSize,
		});

		const newOffset = roundAndConstrainImageZoomOffset(
			sourceImageDimensions,
			picture,
			addPositions(zoomDraggingStart.imageZoomOffset, brickOffset),
		);

		// Press-hold mode: the source showed while the press stayed put; a real move
		// means dragging, so drop back to bricks for the rest of this gesture.
		if (
			canvasSourceOnPressHold &&
			dragSourceActive.current &&
			!arePositionsClose(newOffset, zoomDraggingStart.imageZoomOffset, 0.5)
		) {
			withdrawRenderModeRequest(sourceRenderModeId);
			dragSourceActive.current = false;
		}

		// Deferred mode: drive the canvas live via the callback and defer the picture
		// commit to drag-end, so we don't re-render every picture consumer per move.
		if (onLiveOffset) {
			const previous = pendingLiveOffset.current ?? imageZoomOffset;
			if (arePositionsClose(newOffset, previous, 0.5)) {
				return;
			}
			pendingLiveOffset.current = newOffset;
			onLiveOffset(newOffset);
			return;
		}

		// redux action is very heavy (or maybe it's just the listening consequences)
		// worth preventing it
		if (arePositionsClose(newOffset, imageZoomOffset, 0.5)) {
			return;
		}

		patchPictureDebounced({
			imageZoomOffset: newOffset,
		});
	}
	function onZoomDragEnd() {
		// Flush the deferred offset into the picture once, now the drag is over.
		if (onLiveOffset && pendingLiveOffset.current !== undefined) {
			patchPicture({ imageZoomOffset: pendingLiveOffset.current });
			pendingLiveOffset.current = undefined;
		}
		onEnd?.();
		withdrawRenderModeRequest(sourceRenderModeId);
		dragSourceActive.current = false;
		setZoomDraggingStart(undefined);
	}

	// Two-finger pinch (opt-in). We piggy-back on the same pointer-event stream the
	// drag uses, tracking active touch points so two fingers zoom picture.imageZoom
	// while one finger keeps panning the offset.
	const activeTouches = useRef<Map<number, PixelPosition>>(new Map());
	const pinchStart = useRef<
		{ imageZoom: number; distance: number } | undefined
	>(undefined);

	function onMultiPointerDown(e: TargetedPointerEvent<Element>) {
		if (e.pointerType === "touch") {
			activeTouches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		}

		// Second finger down: switch from dragging to pinching.
		if (activeTouches.current.size >= 2) {
			setZoomDraggingStart(undefined);
			pinchStart.current = {
				imageZoom: picture.imageZoom,
				distance: getTouchDistance(activeTouches.current) ?? 0,
			};
			onStart?.();
			// Zoom never shows the canvas source in press-hold mode; drop any request
			// the preceding one-finger press left active.
			if (canvasSourceOnPressHold) {
				withdrawRenderModeRequest(sourceRenderModeId);
				dragSourceActive.current = false;
			} else {
				requestRenderMode(sourceRenderModeId, "source");
			}
			return;
		}

		onZoomDragStart(e);
	}

	function onMultiPointerMove(e: TargetedPointerEvent<Element>) {
		if (e.pointerType === "touch") {
			activeTouches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		}

		if (activeTouches.current.size >= 2 && pinchStart.current) {
			const distance = getTouchDistance(activeTouches.current);
			if (distance === undefined) {
				return;
			}
			// Distance can briefly be 0 when fingers land on the same point.
			const startDistance =
				pinchStart.current.distance === 0
					? Math.max(distance, 0.1)
					: pinchStart.current.distance;
			const newImageZoom = Math.max(
				1,
				Math.min(
					maxImageZoom,
					pinchStart.current.imageZoom * (distance / startDistance),
				),
			);
			if (Math.abs(picture.imageZoom - newImageZoom) < pinchZoomThreshold) {
				return;
			}
			patchPicture({ imageZoom: newImageZoom });
			return;
		}

		if (zoomDraggingStart && activeTouches.current.size <= 1) {
			onZoomDragMove(e);
		}
	}

	function onMultiPointerUp(e: TargetedPointerEvent<Element>) {
		if (e.pointerType === "touch") {
			activeTouches.current.delete(e.pointerId);
		}

		const touchCount = activeTouches.current.size;

		// Still more than one finger: keep pinching from a fresh baseline.
		if (touchCount >= 2) {
			pinchStart.current = {
				imageZoom: picture.imageZoom,
				distance: getTouchDistance(activeTouches.current) ?? 0,
			};
			return;
		}

		// Pinch -> one finger: hand off to a drag from the remaining finger so
		// panning continues without lifting off.
		if (touchCount === 1) {
			if (pinchStart.current) {
				pinchStart.current = undefined;
				const remaining = Array.from(activeTouches.current.values())[0];
				const rect = e.currentTarget.getBoundingClientRect();
				beginZoomDrag({
					x: remaining.x - rect.left,
					y: remaining.y - rect.top,
				});
			}
			return;
		}

		// No fingers (or a mouse) left: end whatever was happening.
		const wasPinching = pinchStart.current !== undefined;
		pinchStart.current = undefined;
		if (zoomDraggingStart) {
			onZoomDragEnd();
		} else if (wasPinching) {
			onEnd?.();
			withdrawRenderModeRequest(sourceRenderModeId);
		}
	}

	const zoomFinishRenderTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	// Clear the wheel-zoom "show source" timeout on unmount so it can't fire
	// withdrawRenderModeRequest against an unmounted component.
	useEffect(
		() => () => {
			if (zoomFinishRenderTimeoutRef.current !== undefined) {
				clearTimeout(zoomFinishRenderTimeoutRef.current);
			}
		},
		[],
	);

	function onWheel(e: TargetedWheelEvent<EventTarget>) {
		if (e.deltaY === 0) {
			return;
		}

		// We try and make the zoom in and out happen where the focus of the image is. i.e.
		// if there's no image zoom offset then changing the zoom alone is enough. If there
		// is an image zoom offset, then we want to change the offset to be proportional
		const direction = e.deltaY / Math.abs(e.deltaY);
		const newImageZoom = Math.min(
			maxImageZoom,
			Math.max(1, picture.imageZoom + direction * imageZoomStep),
		);

		// Note that patchPicture has some clamping of zoom offsets, so don't need to do that
		// work here (to make sure zoom image offsets don't go outside of bounds).
		const oldZoomFreeBricks = getImageZoomOffsetFree(
			sourceImageDimensions,
			picture,
		);
		const oldOffsetRatios = {
			x:
				oldZoomFreeBricks.widthDimensionMax === 0
					? 0
					: imageZoomOffset.x / oldZoomFreeBricks.widthDimensionMax,
			y:
				oldZoomFreeBricks.heightDimensionMax === 0
					? 0
					: imageZoomOffset.y / oldZoomFreeBricks.heightDimensionMax,
		};
		const newZoomFreeBricks = getImageZoomOffsetFree(sourceImageDimensions, {
			numberOfBasePlates: picture.numberOfBasePlates,
			basePlateSize: picture.basePlateSize,
			imageZoom: newImageZoom,
		});
		const newImageZoomOffsets = {
			x: oldOffsetRatios.x * newZoomFreeBricks.widthDimensionMax,
			y: oldOffsetRatios.y * newZoomFreeBricks.heightDimensionMax,
		};
		patchPicture({
			imageZoom: newImageZoom,
			imageZoomOffset: roundAndConstrainImageZoomOffset(
				sourceImageDimensions,
				picture,
				newImageZoomOffsets,
			),
		});

		// Press-hold mode never shows the canvas source on zoom (the caller drives its
		// own ghost off imageZoom changes), so skip the render-mode dance entirely.
		if (canvasSourceOnPressHold) {
			return;
		}

		// Mouse wheel comes through as discrete events. We want to show source image "while"
		// zooming with the mouse. So we use 300ms buffer while zooming with wheel.
		requestRenderMode(wheelImageZoomSourceRenderId, "source");
		const { current: previousWithdrawTimeoutId } = zoomFinishRenderTimeoutRef;
		if (previousWithdrawTimeoutId !== undefined) {
			clearTimeout(previousWithdrawTimeoutId);
		}
		zoomFinishRenderTimeoutRef.current = setTimeout(() => {
			withdrawRenderModeRequest(wheelImageZoomSourceRenderId);
			zoomFinishRenderTimeoutRef.current = undefined;
		}, 300);
	}

	const isDragging = !!zoomDraggingStart;

	if (enableTwoFingerZoom) {
		// Always bind every pointer handler so a second finger landing mid-drag is
		// seen; the handlers branch internally on touch count.
		return {
			isDragging,
			imageZoomCanvasHandlers: {
				onPointerDown: onMultiPointerDown,
				onPointerMove: onMultiPointerMove,
				onPointerUp: onMultiPointerUp,
				onPointerLeave: onMultiPointerUp,
				onPointerCancel: onMultiPointerUp,
				onWheel,
			},
		};
	}

	return {
		isDragging,
		imageZoomCanvasHandlers: isDragging
			? {
					onPointerMove: onZoomDragMove,
					onPointerLeave: onZoomDragEnd,
					onPointerCancel: onZoomDragEnd,
					onPointerUp: onZoomDragEnd,
				}
			: {
					onPointerDown: onZoomDragStart,
					onWheel,
				},
	};
}

export default useImageZoomCanvasControls;
